import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtDate, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Download, Trash2, FolderArchive, AlertTriangle, Eye, Share2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const PASTAS = [
  { key: "IMOBILIARIO_LEGAL", label: "Imobiliário & Legal" },
  { key: "LICENCAS_ALVARAS", label: "Licenças & Alvarás" },
  { key: "ENGENHARIA_PLANTAS", label: "Engenharia & Plantas" },
  { key: "CERTIDOES_CONTABILIDADE", label: "Certidões & Contabilidade" },
  { key: "CONTRATOS_LOJISTAS", label: "Contratos Lojistas" },
  { key: "OUTROS", label: "Outros" },
];

export default function AdminDocumentos() {
  const [pastas, setPastas] = useState([]);
  const [pastaAtiva, setPastaAtiva] = useState("IMOBILIARIO_LEGAL");
  const [docs, setDocs] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ nome: "", pasta: pastaAtiva, vencimento: "", observacoes: "" });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [p, a] = await Promise.all([
      api.get("/documentos/pastas"),
      api.get("/documentos/alertas/vencimento"),
    ]);
    setPastas(p.data);
    setAlertas(a.data);
    const d = await api.get(`/documentos?pasta=${pastaAtiva}`);
    setDocs(d.data);
  }, [pastaAtiva]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!fileRef.current?.files?.[0]) {
      toast.error("Selecione um arquivo");
      return;
    }
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    fd.append("nome", uploadForm.nome || fileRef.current.files[0].name);
    fd.append("pasta", uploadForm.pasta);
    if (uploadForm.vencimento) fd.append("vencimento", new Date(uploadForm.vencimento).toISOString());
    if (uploadForm.observacoes) fd.append("observacoes", uploadForm.observacoes);
    try {
      await api.post("/documentos/upload", fd);
      toast.success("Documento enviado com sucesso");
      setModalOpen(false);
      setUploadForm({ nome: "", pasta: pastaAtiva, vencimento: "", observacoes: "" });
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha no upload");
    }
  };

  const download = async (doc) => {
    try {
      const resp = await api.get(`/documentos/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: doc.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro no download");
    }
  };

  const visualizar = async (doc) => {
    try {
      const resp = await api.get(`/documentos/${doc.id}/view`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: doc.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Não revoga imediatamente para deixar o navegador carregar
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Erro ao abrir documento");
    }
  };

  const compartilhar = async (doc) => {
    try {
      const resp = await api.get(`/documentos/${doc.id}/download`, { responseType: "blob" });
      const file = new File([resp.data], doc.original_filename, { type: doc.content_type || "application/octet-stream" });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: doc.nome,
          text: `Documento: ${doc.nome}`,
          files: [file],
        });
      } else {
        // Fallback: copiar como blob e baixar (compartilhamento nativo não disponível)
        const blob = new Blob([resp.data], { type: doc.content_type });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.success("Documento aberto em nova aba — copie o link para compartilhar");
      }
    } catch {
      toast.error("Falha ao compartilhar");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este documento?")) return;
    await api.delete(`/documentos/${id}`);
    toast.success("Excluído");
    load();
  };

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="label-tag mb-2">Gestão eletrônica de documentos · GED</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Documentos</h1>
      </div>

      {alertas.length > 0 && (
        <Card className="p-5 rounded-sm border-warning/40 bg-warning/5">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle size={16} className="text-warning" />
            <span className="font-semibold text-sm">Alertas de vencimento próximos ({alertas.length})</span>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {alertas.slice(0, 6).map((a) => (
              <div key={a.id} className="text-xs border border-warning/30 bg-background p-2 rounded-sm" data-testid={`alerta-${a.id}`}>
                <div className="font-semibold truncate">{a.nome}</div>
                <div className="font-mono text-muted-foreground">
                  {a.pasta_label} · {a.dias_para_vencer < 0 ? `${Math.abs(a.dias_para_vencer)}d vencido` : `${a.dias_para_vencer}d restantes`}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Tabs value={pastaAtiva} onValueChange={setPastaAtiva}>
        <TabsList className="rounded-sm h-auto flex-wrap justify-start">
          {PASTAS.map((p) => {
            const c = pastas.find((x) => x.key === p.key)?.count || 0;
            return (
              <TabsTrigger
                key={p.key}
                value={p.key}
                data-testid={`tab-pasta-${p.key}`}
                className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background"
              >
                <FolderArchive size={13} className="mr-2" />
                {p.label}
                <Badge variant="outline" className="ml-2 text-[10px] border-current">{c}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {PASTAS.map((p) => (
          <TabsContent key={p.key} value={p.key} className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl">{p.label}</h2>
              <Dialog open={modalOpen && uploadForm.pasta === p.key} onOpenChange={(o) => { setModalOpen(o); if (o) setUploadForm({ ...uploadForm, pasta: p.key }); }}>
                <DialogTrigger asChild>
                  <Button data-testid={`upload-${p.key}`} className="rounded-sm bg-foreground text-background hover:bg-foreground/90">
                    <Upload size={14} className="mr-2" /> Novo upload
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm">
                  <DialogHeader><DialogTitle>Enviar documento</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="label-tag">Arquivo (máx 50MB)</Label>
                      <Input data-testid="upload-file" type="file" ref={fileRef} />
                    </div>
                    <div>
                      <Label className="label-tag">Nome do documento</Label>
                      <Input data-testid="upload-nome" value={uploadForm.nome} onChange={(e) => setUploadForm({ ...uploadForm, nome: e.target.value })} placeholder="Ex: Alvará Bombeiros 2026" />
                    </div>
                    <div>
                      <Label className="label-tag">Pasta</Label>
                      <Select value={uploadForm.pasta} onValueChange={(v) => setUploadForm({ ...uploadForm, pasta: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PASTAS.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-tag">Vencimento (se aplicável)</Label>
                      <Input data-testid="upload-venc" type="date" value={uploadForm.vencimento} onChange={(e) => setUploadForm({ ...uploadForm, vencimento: e.target.value })} />
                    </div>
                    <div>
                      <Label className="label-tag">Observações</Label>
                      <Textarea data-testid="upload-obs" value={uploadForm.observacoes} onChange={(e) => setUploadForm({ ...uploadForm, observacoes: e.target.value })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                    <Button data-testid="upload-enviar" onClick={upload} className="bg-foreground text-background hover:bg-foreground/90">Enviar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {docs.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-sm">
                Nenhum documento nesta pasta.
              </div>
            ) : (
              <div className="grid gap-2">
                {docs.map((d) => (
                  <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between border border-border p-4 rounded-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{d.nome}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {d.original_filename} · {(d.size / 1024).toFixed(0)}KB
                      </div>
                      {d.vencimento && (
                        <Badge variant="outline" className="mt-1 text-[10px]">
                          Vence em {fmtDate(d.vencimento)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="icon" variant="outline" onClick={() => visualizar(d)} data-testid={`view-${d.id}`} title="Visualizar">
                        <Eye size={14} />
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => download(d)} data-testid={`dl-${d.id}`} title="Baixar">
                        <Download size={14} />
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => compartilhar(d)} data-testid={`share-${d.id}`} title="Compartilhar">
                        <Share2 size={14} />
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => del(d.id)} data-testid={`del-${d.id}`} title="Excluir">
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
